---
title: Support & Troubleshooting
sidebar_position: 7
description: Support & Troubleshooting
---

# Support & Troubleshooting

## Common Issues and Solutions

#### Issue: Empty Observations Display

**Symptoms**:

- No satellite images appear in the Observations Widget
- Message: "No observations available"

**Possible Causes and Solutions**:

1. **Time Range Doesn't Match Data Availability**:
   - **Solution**: Open the **side panel** and go to **Settings** to adjust your time range
   - Check that the date range includes periods when satellite data was collected
   - Try expanding the date range or selecting a different period

2. **Selected Layer Not Available**:
   - **Solution**: Switch to a different observation layer in the **observations footer**
   - Some layers may not be available for all time periods

3. **Imagery Processing Error**:
   - **Symptom**: An error message appears inside the observation tile instead of an image
   - **Solution**: The message comes directly from the satellite imagery processing service — read it for specific guidance (e.g. invalid configuration, data gap, area too large)
   - If the message is unclear or persists, contact your administrator with the exact text shown

---

#### Issue: Cannot See FOI

**Symptoms**:

- FOI doesn't appear on the map or in the list
- Search returns no results

**Possible Causes and Solutions**:

1. **Scope Permissions**:
   - **Solution**: Verify you have access to the correct scope
   - Contact your administrator to request scope permissions
   - Switch to a scope where you have access

2. **Zoom Level Too Far Out**:
   - **Solution**: Zoom in on the map
   - FOIs may not display until you reach the zoom threshold (typically zoom level 12)

3. **Filters Applied**:
   - **Solution**: Clear any active filters in the FOI List
   - Reset search criteria
   - Check if filters are excluding your FOI

4. **Incorrect FOI ID**:
   - **Solution**: Verify the FOI ID is correct
   - Use the full reference ID format (e.g., DEMO.FOI.6999931001)

5. **Layer not Displayed**:
   - layer might be toggled off in the map layer selector
   - **Solution**: Open the **map layer selector** and ensure the FOI type layer is toggled on

---

#### Issue: Email Verification Timeout

**Symptoms**:

- Verification email not received
- Link expired (5-minute timeout)

**Possible Causes and Solutions**:

1. **5-Minute Window Expired**:
   - **Solution**: Restart the login process
   - Request a new verification email
   - Check email immediately and click the link within 5 minutes

2. **Email in Spam/Junk Folder**:
   - **Solution**: Check your spam or junk mail folder
   - Look for email from noreply@planet.com
   - Mark as "Not Spam" to receive future emails

3. **Email Address Incorrect**:
   - **Solution**: Verify your email address is correct in your Planet account
   - Update email address and try again

---

#### Issue: Signals/Markers Not Displaying

**Symptoms**:

- Signal/Marker Visualization graph is empty
- No lines or markers appear

**Possible Causes and Solutions**:

1. **Signals/Markers Hidden in Legend**:
   - **Solution**: Click legend items to toggle visibility on
   - Look for colored squares (active) vs. gray (inactive)
   - Enable the signals/markers you want to view

2. **Marker Context Not Selected**:
   - **Solution**: Open the **side panel** and go to **Settings**, then select a marker context under the **Markers** section
   - Ensure the context has calculated markers for the time period

3. **Time Range Issue**:
   - **Solution**: Open the **side panel** and go to **Settings** to adjust your time range to include periods with signal data
   - Signals are only available for dates with satellite observations

---

#### Issue: Bundle Creation Status is FAILED

**Symptoms**:

- A bundle in the **Validation Console** shows status **FAILED** immediately after creation
- No packages or FOIs are assigned

**Possible Causes and Solutions**:

1. **Filters match zero FOIs**:
   - If the query, CSV FOI list, or CSV reference ID list resolves to no valid FOIs, the bundle fails.
   - **Solution**: Check each filter input:
     - **Query**: broaden or correct the query conditions.
     - **CSV FOI list**: confirm the IDs are valid and belong to the current scope.
     - **CSV reference ID list**: confirm the reference IDs exist and are of the selected FOI type.
   - After adjusting, create a new bundle — failed bundles cannot be retried in place.

2. **Query is too broad (opposite problem)**:
   - A query that matches tens of thousands of FOIs can push the package count above 1000 even with a reasonable package size.
   - **Solution**: Narrow the query to bring the total FOI count to a range where package count stays within limit.

---

#### Issue: Map Base Layer Not Loading or Has Wrong Name

If a base layer fails to load or its name looks incorrect or outdated, the layer configuration is managed at the scope level — it is not controlled by individual users. Report the problem to your **scope administrator** and include the layer name you see in the map layer selector.

## Best Practices

#### Always Synchronize Time Range with Available Data

- Before analyzing an FOI, check what time periods have data
- Set your time range to match the growing season or monitoring period
- Don't assume data is available for all dates

#### Use Appropriate Layer for Analysis Needs

- **True Color**: For natural appearance and visual interpretation
- **False Color**: For vegetation density analysis
